import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/hooks/useAuth';
import { isNewPasswordRequired } from '../features/auth/util/authGuard';
import { useIsPortraitMobile } from '../hooks/useIsPortraiteMobile';
import TiltMessage from '../components/feedback/TiltMessage';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionMessage, setSessionMessage] = React.useState<string | null>(null);
  const [showReset, setShowReset] = useState(false);
  const [_resetStep, setResetStep] = useState(1);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const navigate = useNavigate();
  const { login, confirmPasswordReset } = useAuth();

  React.useEffect(() => {
    const msg = localStorage.getItem('sessionMessage');
    if (msg) {
      setSessionMessage(msg);
      localStorage.removeItem('sessionMessage');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await login(username, password);
      if (isNewPasswordRequired(res)) {
        navigate('/change-password');
      } else {
        navigate('/overview');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid username or password');
    } finally {
      setIsLoading(false);
    }
  };

  const showTiltMessage = useIsPortraitMobile();

  return (
    <><TiltMessage show={showTiltMessage} />
      <div className={showTiltMessage ? "blur-sm pointer-events-none" : ""}>
        <div className="flex min-h-screen">
          {/* Left side - Login form (30%) */}
          <div className="w-full sm:w-2/5 lg:w-1/3 xl:w-1/4 flex items-center justify-center bg-gray-900 px-4 py-8">
            <div className="w-full max-w-sm space-y-6">
 
              {sessionMessage && (
                <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-3">
                  <p className="text-yellow-400 text-sm text-center">{sessionMessage}</p>
                </div>
              )}

              {error && (
                <div className="bg-red-900/30 border border-red-600 rounded-lg p-3">
                  <p className="text-red-400 text-sm text-center">{error}</p>
                </div>
              )}

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-gray-200 tracking-wide"
                  >
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-4 py-3.5 bg-gray-800/80 border border-gray-600/50 rounded-xl text-white 
                              placeholder-gray-400 focus:outline-none focus:ring-2 focus:[#234D8B] focus:[#234D8B] 
                                transition-all duration-300 hover:border-gray-500 hover:bg-gray-800 backdrop-blur-sm"
                    placeholder="Enter your username"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-200 tracking-wide"
                  >
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3.5 bg-gray-800/80 border border-gray-600/50 rounded-xl text-white 
                              placeholder-gray-400 focus:outline-none focus:ring-2 focus:[#234D8B] focus:[#234D8B] 
                              transition-all duration-300 hover:border-gray-500 hover:bg-gray-800 backdrop-blur-sm"
                    placeholder="Enter your password"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    className="text-xs text-blue-400 hover:underline"
                    onClick={() => setShowReset(true)}
                    disabled={isLoading}
                  >
                    Forgot Password?
                  </button>
                </div>

                <div className="flex justify-center items-center w-full pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="group relative w-full max-w-xs py-4 px-8 bg-gradient-to-r from-[#234D8B] via-[#4469A4] to-[#234D8B] text-white text-lg 
                                font-semibold rounded-xl transition-all duration-300 hover:bg-gradient-to-r hover:from-[#234D8B] hover:via-[#4469A4] 
                                hover:to-[#234D8B] focus:outline-none focus:ring-2 focus:[#234D8B] focus:ring-offset-2 focus:ring-offset-gray-900 transform 
                                hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed 
                                disabled:transform-none disabled:shadow-none overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <span className="relative z-10">
                      {isLoading ? (
                        <div className="flex items-center justify-center space-x-3">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Signing in...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2 transition-colors duration-300">
                          <span className="transition-colors duration-300 group-hover:text-white">Sign In</span>
                          <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      )}
                    </span>
                  </button>
                </div>
              </form>

              {showReset && (
                <div className="bg-gray-900/90 border border-gray-700 rounded-lg p-4 mt-4">
                  <>
                    <div className="mb-3 text-xs text-yellow-400 text-center">
                      To reset your password, please ask an admin to send you a reset code. Enter your username, the code from your email, and your new password below.
                    </div>
                    <label className="block text-sm text-gray-200 mb-2">Username</label>
                    <input
                      type="text"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      className="w-full px-3 py-2 rounded bg-gray-800 text-white mb-2"
                      placeholder="Enter your username"
                      autoComplete="username"
                    />
                    <label className="block text-sm text-gray-200 mb-2">Reset Code</label>
                    <input
                      type="text"
                      value={resetCode}
                      onChange={e => setResetCode(e.target.value)}
                      className="w-full px-3 py-2 rounded bg-gray-800 text-white mb-2"
                      placeholder="Enter the code from your email"
                    />
                    <label className="block text-sm text-gray-200 mb-2">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 rounded bg-gray-800 text-white mb-2"
                      placeholder="Enter your new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="w-full py-2 bg-green-600 text-white rounded"
                      onClick={async () => {
                        setResetError('');
                        setResetSuccess('');
                        if (!username || username.trim().length < 1) {
                          setResetError('Please enter your username.');
                          return;
                        }
                        try {
                          await confirmPasswordReset(username.trim(), resetCode, newPassword);
                          setResetSuccess('Password has been reset. You can now log in.');
                          setShowReset(false);
                          setResetStep(1);
                        } catch (err: any) {
                          setResetError(err.message || 'Failed to reset password.');
                        }
                      }}
                    >
                      Reset Password
                    </button>
                    {resetError && <div className="text-red-400 mt-2">{resetError}</div>}
                    {resetSuccess && <div className="text-green-400 mt-2">{resetSuccess}</div>}
                  </>
                  <button
                    type="button"
                    className="mt-3 text-xs text-gray-400 hover:underline"
                    onClick={() => {
                      setShowReset(false);
                      setResetStep(1);
                      setResetError('');
                      setResetSuccess('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right side - TenoreBG background (70%) */}
          <div className="hidden sm:block sm:w-3/5 lg:w-2/3 xl:w-3/4 bg-black relative overflow-hidden">
            {/* Video Background */}
            <video
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src="https://cdn.shopify.com/videos/c/o/v/4cc30c79d28e401daa75d6d4617e8a26.mp4" type="video/mp4" />
            </video>

            {/* Video Overlay */}
            <div className="absolute inset-0 bg-gradient-to-l from-black/30 via-transparent to-gray-900/40"></div>

            {/* Text Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-8">
                <div className="flex justify-center mb-4">
                  <img src="/tenorelogo.svg" alt="Tenore Logo" className="w-48 h-auto" />
                </div>
                <p className="text-xs sm:text-base text-white font-light tracking-wide font-sans">
                  HIGHEST OF THE ORDINARY
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;